// frontend/src/components/ShoutoutWallModal.js

import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  CaretCircleLeft,
  CaretCircleRight,
  MegaphoneSimple,
  X,
} from "phosphor-react";

import { api } from "../api";
import { DEFAULT_PROFILE_IMAGE } from "../utils/blank-image";
import { getShoutoutFont } from "../constants/shoutoutFonts";

const SHOUTOUTS_PER_PAGE = 12;

const YEARBOOK_POSITIONS = [
  // 1 — LEFT 1
  {
    left: "3%",
    top: "10%",
    rotate: "-2deg",
  },

  // 2 — RIGHT 1
  {
    right: "3%",
    top: "10%",
    rotate: "2deg",
  },

  // 3 — LEFT 2
  {
    left: "8%",
    top: "34%",
    rotate: "1deg",
  },

  // 4 — RIGHT 2
  {
    right: "8%",
    top: "34%",
    rotate: "-2deg",
  },

  // 5 — LEFT 3
  {
    left: "4%",
    top: "57%",
    rotate: "-3deg",
  },

  // 6 — RIGHT 3
  {
    right: "3%",
    top: "53%",
    rotate: "3deg",
  },

  // 7 — LEFT 4
  {
    left: "3%",
    top: "79%",
    rotate: "3deg",
  },

  // 8 — RIGHT 4
  {
    right: "5%",
    top: "82%",
    rotate: "-2deg",
  },

  // 9 — TOP 1
  {
    left: "35%",
    top: "3%",
    rotate: "-1deg",
  },

  // 10 — BOTTOM 1
  {
    left: "37%",
    bottom: "5%",
    rotate: "2deg",
  },

  // 11 — TOP 2
  {
    left: "37%",
    top: "18%",
    rotate: "3deg",
  },

  // 12 — BOTTOM 2
  {
    left: "34%",
    bottom: "28%",
    rotate: "-1deg",
  },
];

function ShoutoutSignature({
  shoutout,
  senderName,
  shoutoutFont,
}) {
  const prefixRef = useRef(null);
  const [contentWidth, setContentWidth] = useState(null);

  useLayoutEffect(() => {
    if (!shoutout.prefix || !prefixRef.current) {
      setContentWidth(210);
      return;
    }

    const width =
      prefixRef.current.getBoundingClientRect().width;

    setContentWidth(Math.ceil(width));
  }, [shoutout.prefix]);

  return (
    <div
      className="
        flex flex-col
        items-center justify-center
        text-center
      "
      style={{
        width: contentWidth
          ? `${Math.max(contentWidth, 200)}px`
          : "180px",
      }}
    >
      {shoutout.prefix && (
        <p
          ref={prefixRef}
          className="
            whitespace-nowrap
            text-[13px] font-bold
            uppercase
            leading-none
            tracking-[0.03em]
            text-blue-500
          "
        >
          {shoutout.prefix}
        </p>
      )}

      <p
        className="
          mt-1
          w-full
          whitespace-pre-wrap
          break-words
          text-[17px]
          leading-[1.1]
          text-[#25366f]
        "
        style={{
          fontFamily: shoutoutFont.css,
          fontWeight: shoutoutFont.weight,
        }}
      >
        {shoutout.body}
      </p>

      <p className="mt-1 text-[10px] font-medium leading-none text-gray-400">
        @{senderName}
      </p>
    </div>
  );
}

export default function ShoutoutWallModal({
  open,
  onClose,
  profileUser,
  canGiveShoutout = false,
  onGiveShoutout,
  refreshKey = 0,

  // Optional local/demo data
  // When supplied, the modal does not call the API
  shoutoutsOverride,
}) {
  const [shoutouts, setShoutouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);

  const displayName =
    profileUser?.display_name ||
    profileUser?.name ||
    profileUser?.username ||
    "this person";

  const profilePhoto =
    profileUser?.profile_pic_url ||
    DEFAULT_PROFILE_IMAGE;

  useEffect(() => {
    if (!open || !profileUser?.id) return;

    // PREVIEW / LOCAL MODE
    if (Array.isArray(shoutoutsOverride)) {
      setShoutouts(shoutoutsOverride);
      setLoading(false);
      setError("");
      return;
    }

    let cancelled = false;

    const loadShoutouts = async () => {
      setLoading(true);
      setError("");

      try {
        const token =
          localStorage.getItem("token") ||
          sessionStorage.getItem("token");

        const response = await api.get(
          `/users/${profileUser.id}/flowers`,
          {
            headers: token
              ? {
                  Authorization: `Bearer ${token}`,
                }
              : undefined,
          }
        );

        if (!cancelled) {
          setShoutouts(
            Array.isArray(response.data)
              ? response.data
              : []
          );
        }
      } catch (err) {
        if (!cancelled) {
          setShoutouts([]);

          setError(
            err?.response?.data?.detail ||
              "Could not load shoutouts."
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadShoutouts();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    profileUser?.id,
    refreshKey,
    shoutoutsOverride,
  ]);

  const visibleShoutouts = useMemo(
    () =>
      shoutouts.filter(
        (shoutout) =>
          !shoutout.hidden_by_recipient
      ),
    [shoutouts]
  );

  const totalPages = Math.max(
    1,
    Math.ceil(
      visibleShoutouts.length /
        SHOUTOUTS_PER_PAGE
    )
  );

  /*
   * If somebody deletes/hides shoutouts and we were on
   * a page that no longer exists, move back automatically.
   */
  useEffect(() => {
    if (page > totalPages - 1) {
      setPage(Math.max(0, totalPages - 1));
    }
  }, [page, totalPages]);

  useEffect(() => {
    if (!open) return;

    if (refreshKey > 0) {
      setPage(Math.max(0, totalPages - 1));
    } else {
      setPage(0);
    }
  }, [open, profileUser?.id, refreshKey, totalPages]);

  const pageShoutouts = useMemo(() => {
    const chronologicalShoutouts = [
      ...visibleShoutouts,
    ].reverse();

    const start =
      page * SHOUTOUTS_PER_PAGE;

    const items = chronologicalShoutouts.slice(
      start,
      start + SHOUTOUTS_PER_PAGE
    );

    // 8 or fewer:
    // preserve normal newest-first ordering.
    if (items.length <= 8) {
      return items;
    }

    /*
    * LEFT/RIGHT slots = first 8 positions
    * TOP/BOTTOM slots = everything after that
    *
    * Put the shortest shoutouts into the
    * top/bottom positions
    */
    const topBottomCount = items.length - 8;

    const rankedByLength = items
      .map((shoutout, originalIndex) => ({
        shoutout,
        originalIndex,
        length:
          (shoutout.prefix?.length || 0) +
          (shoutout.body?.length || 0),
      }))
      .sort((a, b) => a.length - b.length);

    const topBottomIndexes = new Set(
      rankedByLength
        .slice(0, topBottomCount)
        .map((item) => item.originalIndex)
    );

    // Everyone else stays on left/right,
    // preserving their original order.
    const sideShoutouts = items.filter(
      (_, index) => !topBottomIndexes.has(index)
    );

    // Shortest messages go top/bottom.
    const topBottomShoutouts = rankedByLength
      .slice(0, topBottomCount)
      .map((item) => item.shoutout);

    return [
      ...sideShoutouts,
      ...topBottomShoutouts,
    ];
  }, [visibleShoutouts, page]);

  const goPrevious = () => {
    setPage((current) =>
      Math.max(0, current - 1)
    );
  };

  const goNext = () => {
    setPage((current) =>
      Math.min(
        totalPages - 1,
        current + 1
      )
    );
  };

  if (!open) return null;

  return createPortal(
    <div
      className="
        fixed inset-0 z-[9999]
        flex items-center justify-center
        p-4
      "
    >
      {/* Dimmed profile behind modal */}
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-label="Close shoutouts"
      />

      <div
        className="
          relative z-10
          flex h-[72vh] w-full max-w-3xl
          flex-col overflow-hidden
          rounded-[32px]
          bg-blue-50 ring-4 ring-blue-800
        "
      >
        {/* HEADER */}
        <div
          className="
            relative z-20
            shrink-0
            px-5 pb-2 pt-5
            sm:px-8
          "
        >
          {/* TITLE + SQUIGGLE + BUTTON */}
          <div className="flex items-start justify-between gap-4 pr-10">
            <div className="inline-flex flex-col items-stretch">
              <h2
                className="
                  text-2xl font-black
                  tracking-[-0.04em]
                  text-gray-900
                "
              >
                shoutouts for {displayName}
              </h2>

              <svg
                viewBox="0 0 300 16"
                className="-mt-0.5 h-3 w-full text-blue-500"
                aria-hidden="true"
                preserveAspectRatio="none"
              >
                <path
                  d="
                    M 0 8
                    Q 15 0, 30 8
                    T 60 8
                    T 90 8
                    T 120 8
                    T 150 8
                    T 180 8
                    T 210 8
                    T 240 8
                    T 270 8
                    T 300 8
                  "
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="5"
                  strokeLinecap="round"
                />
              </svg>
            </div>

            {canGiveShoutout && (
              <button
                type="button"
                onClick={onGiveShoutout}
                className="
                  mt-2 shrink-0
                  inline-flex items-center gap-1
                  !min-h-0 !min-w-0
                  rounded-full bg-blue-300
                  px-3 py-1 ring-2 ring-blue-800
                  text-sm font-semibold text-blue-800
                  hover:animate-[wiggle_1s_ease-in-out_infinite]
                "
              >
                <MegaphoneSimple
                  size={16}
                  weight="duotone"
                />
                Give a shoutout
              </button>
            )}
          </div>

          {/* CLOSE — FIXED TO TOP-RIGHT OF HEADER */}
          <button
            type="button"
            onClick={onClose}
            className="
              absolute right-5 top-5
              inline-flex items-center justify-center
              !min-h-0 !min-w-0
              sm:right-6
            "
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* BODY */}
        {loading ? (
          <div className="flex flex-1 items-center justify-center">
            <p className="text-sm text-gray-500">
              Loading shoutouts…
            </p>
          </div>
        ) : error ? (
          <div className="flex flex-1 items-center justify-center">
            <p className="text-sm text-red-600">
              {error}
            </p>
          </div>
        ) : visibleShoutouts.length === 0 ? (
          <div
            className="
              flex flex-1 flex-col
              items-center justify-center
              pb-10 text-center
            "
          >
            <img
              src={profilePhoto}
              alt={displayName}
              className="
                h-24 w-24
                rounded-full object-cover
                sm:h-28 sm:w-28
              "
            />
          </div>
        ) : (
          <div
            className="
              relative min-h-0 flex-1
              overflow-hidden
              mx-4 mb-4
              rounded-[24px]
              bg-blue-50
              sm:mx-6
            "
          >
            {/* DESKTOP YEARBOOK PAGE */}
            <div className="relative hidden h-full w-full md:block">

              {/* CENTER PROFILE */}
              <div
                className="
                  pointer-events-none
                  absolute left-1/2 top-[47%]
                  z-20 flex
                  -translate-x-1/2
                  -translate-y-1/2
                  flex-col
                  items-center justify-center
                "
              >
                <div className="rounded-full p-1.5">
                  <img
                    src={profilePhoto}
                    alt={displayName}
                    className="
                      h-24 w-24
                      rounded-full object-cover
                      sm:h-28 sm:w-28
                    "
                  />
                </div>
              </div>

              {/* SHOUTOUT SIGNATURES */}
              {pageShoutouts.map((shoutout, index) => {
                const senderName =
                  shoutout?.sender?.username ||
                  shoutout?.sender?.name ||
                  "someone";

                const shoutoutFont = getShoutoutFont(
                  shoutout.font_id
                );

                const position =
                  YEARBOOK_POSITIONS[
                    index % YEARBOOK_POSITIONS.length
                  ];

                const transformParts = [];

                if (position.transformX) {
                  transformParts.push(
                    `translateX(${position.transformX})`
                  );
                }

                transformParts.push(
                  `rotate(${position.rotate})`
                );

                return (
                  <div
                    key={shoutout.id}
                    className="
                      absolute z-10
                      flex flex-col
                      items-center justify-center
                      text-center
                    "
                    style={{
                      left: position.left,
                      right: position.right,
                      top: position.top,
                      bottom: position.bottom,
                      transform: transformParts.join(" "),
                    }}
                  >
                    <ShoutoutSignature
                      shoutout={shoutout}
                      senderName={senderName}
                      shoutoutFont={shoutoutFont}
                    />
                  </div>
                );
              })}
            </div>

            {/* MOBILE / SMALL SCREEN */}
            <div
              className="
                flex h-full flex-col
                overflow-y-auto
                px-4 py-5
                md:hidden
              "
            >
              {/* Keep person centered first */}
              <div className="shrink-0 text-center">
                <img
                  src={profilePhoto}
                  alt={displayName}
                  className="
                    h-24 w-24
                    rounded-full object-cover
                    sm:h-28 sm:w-28
                  "
                />

                <p
                  className="
                    mt-2 text-base
                    font-black
                    tracking-[-0.03em]
                    text-gray-900
                  "
                >
                  {displayName}
                </p>
              </div>

              <div
                className="
                  mt-6 grid
                  grid-cols-2 gap-x-5 gap-y-7
                  pb-4
                "
              >
                {pageShoutouts.map(
                  (shoutout, index) => {
                    const senderName =
                      shoutout?.sender?.username ||
                      shoutout?.sender?.name ||
                      "someone";

                    const shoutoutFont = getShoutoutFont(
                      shoutout.font_id
                    );

                    return (
                      <div
                        key={shoutout.id}
                        className={`
                          text-center
                          ${
                            index % 3 === 0
                              ? "-rotate-1"
                              : index % 3 === 1
                                ? "rotate-1"
                                : ""
                          }
                        `}
                      >
                        {shoutout.prefix && (
                          <p
                            className="
                              text-[9px]
                              font-bold uppercase
                              tracking-[0.08em]
                              text-blue-500
                            "
                          >
                            {shoutout.prefix}
                          </p>
                        )}

                        <p
                          className="
                            whitespace-pre-wrap
                            text-[17px]
                            leading-[1.1]
                            text-[#25366f]
                          "
                          style={{
                            fontFamily: shoutoutFont.css,
                            fontWeight: shoutoutFont.weight,
                          }}
                        >
                          {shoutout.body}
                        </p>

                        <p className="mt-1 text-[10px] text-gray-400">
                          @{senderName}
                        </p>
                      </div>
                    );
                  }
                )}
              </div>
            </div>
          </div>
        )}

        {/* PAGE CONTROLS */}
        {!loading &&
          !error &&
          visibleShoutouts.length > 0 &&
          totalPages > 1 && (
            <div
              className="
                flex shrink-0
                items-center justify-center
                gap-3 pb-5
              "
            >
              <button
                type="button"
                onClick={goPrevious}
                disabled={page === 0}
                className="
                  inline-flex
                  !min-h-0 !min-w-0
                  items-center justify-center
                  text-blue-600
                  transition
                  hover:text-blue-800
                  disabled:cursor-default
                  disabled:opacity-25
                "
                aria-label="Previous shoutouts page"
              >
                <CaretCircleLeft
                  size={25}
                  weight="duotone"
                />
              </button>

              <div className="flex items-center gap-1.5">
                {Array.from(
                  { length: totalPages },
                  (_, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() =>
                        setPage(index)
                      }
                      aria-label={`Go to shoutouts page ${
                        index + 1
                      }`}
                      className={`
                        h-1.5 w-1.5
                        !min-h-0 !min-w-0
                        rounded-full
                        transition
                        ${
                          page === index
                            ? "bg-blue-600"
                            : "bg-blue-200 hover:bg-blue-300"
                        }
                      `}
                    />
                  )
                )}
              </div>

              <button
                type="button"
                onClick={goNext}
                disabled={
                  page === totalPages - 1
                }
                className="
                  inline-flex
                  !min-h-0 !min-w-0
                  items-center justify-center
                  text-blue-600
                  transition
                  hover:text-blue-800
                  disabled:cursor-default
                  disabled:opacity-25
                "
                aria-label="Next shoutouts page"
              >
                <CaretCircleRight
                  size={25}
                  weight="duotone"
                />
              </button>
            </div>
          )}
      </div>
    </div>,
    document.body
  );
}